// Higher Order Class to make all network calls
import axios from "axios";
import { Cookies } from "react-cookie";
import { APIWithOfflineRouter, HTTP_METHODS, urlBuilder } from "./httpHelper";
import { APIConfig } from "../config/serverConfig";
import { APIError, APIResponse } from "./responseParser";
import { refreshAuthToken } from "./tokenRefresher";
import { APIAborter } from "./abortController";
import offlineManager from "./offlineManager";
import { HTTP_STATUS } from "./statusCode";
import { CookieKeys } from "../../constants/cookieKeys";
import { apiError, offlineNotation } from "./errorParser";
import { APIRouter } from "./httpHelper";
// import { AsyncStorageUtils } from "@app/Helpers/AsyncStorage"
// import { Stores } from "@app/Redux/Stores/Store"
// import { AppActions } from "@app/Redux/Slices/AppSlice"

//#region Documentation
// ********************
// Create a new Instance of NetworkManager by passing APIRouter argument
// After creating instance, call `request` method to make network request
// Example:
// const payload = {email: "example@gmail.com", password: "123456"}
// const instance = NetworkManager(API.Auth.Login)
// const result = await instance.request(payload)
// --------------------
// You can also pass some id in the url as parameter
// If params are named params then pass an object, the key name must match the param name
// Eg. If the URL is like "https://example.com/login?type=regular", then request would look like below
// const result = await instance.request(payload, {type: "regular"})
// --------------------
// If the params are not named then pass an array of data
// Eg. If the URL is like "https://example.com/user/1/2", then request would look like below
// const result = await instance.request(payload, ["id1", "id2"])
// ********************

//#region  Main Function
export default async function networkManager(
  router: APIRouter,
  withFile: boolean = false
) {
  const { TIMEOUT, API_AUTH_HEADER, AUTH_TYPE, CONTENT_TYPE } = APIConfig;
  const REQ_CONTENT_TYPE = withFile
    ? CONTENT_TYPE.MULTIPART
    : CONTENT_TYPE.JSON;

  axios.defaults.baseURL = router.baseURL;
  axios.defaults.timeout = TIMEOUT;
  axios.defaults.headers.common["Content-Type"] = REQ_CONTENT_TYPE;
  axios.defaults.headers.common["Accept-Language"] = "en";

  let refreshCount = 0;

  //#region  Network Call
  /**
   * Asynchronous function that sends a request to an API and returns a promise that resolves to an APIResponse object.
   *
   * @param body - The request body as a Record<string, any> object. Default is an empty object.
   * @param params - The request parameters as an object or an array. Default is an empty object.
   * @returns A promise that resolves to an APIResponse object.
   */
  async function request<T>(
    body: Record<string, any> = {},
    params = {} || []
  ): Promise<APIResponse<T>> {
    const url = urlBuilder(router, params);
    const getHttpMethod = router.method !== HTTP_METHODS.GET;
    const getArrayParams = !Array.isArray(params) && Object.keys(params).length;
    const httpBody = httpPayloadBuilder(body, withFile);

    // extract authToken from storage
    // const cookie = new Cookies();

    const authToken = localStorage.getItem("user-token");
    if (authToken && authToken !== "undefined") {
      axios.defaults.headers.common[
        API_AUTH_HEADER
      ] = `${AUTH_TYPE} ${authToken}`;
    }

    try {
      const result = await axios.request({
        signal: APIAborter.initiate().signal,
        url: url,
        method: router.method,
        ...(getHttpMethod && { data: httpBody }),
        ...(getArrayParams && { params: params }),
      });

      const response = result.data;
      const success =
        result.data.statusCode === 200 || result.data.statusCode === 201;
      return new APIResponse(
        response.data,
        success,
        result.data.statusCode,
        response.message
      );
    } catch (err: any) {
      // Catch all errors
      // const error = err as AxiosError<any, any>;
      apiError(err?.response?.data?.error?.message);
      const IsNetworkError = err.code === HTTP_STATUS.NETWORK_ERR;
      const IsOfflineRouter = router instanceof APIWithOfflineRouter;

      if (IsOfflineRouter && IsNetworkError) {
        offlineNotation();
        return offlineManager(router.offlineJson);
      }

      // If token expired, get it refreshed
      //#region  401 Handler
      /**
       * This code snippet handles the error response in case of a 401 status code or a network error.
       *
       * If the response status is 401, it checks if the refreshCount is less than the maximum refresh attempts defined in APIConfig.
       * If so, it retrieves the refresh token from AsyncStorageUtils and calls the refreshAuthToken function to refresh the authentication token.
       * Then, it increments the refreshCount and returns the control back to the network manager to retry the request.
       *
       * If the refreshCount exceeds the maximum refresh attempts, it dispatches the LOGOUT action from the AppActions and performs the necessary logout actions.
       *
       * If the error code is a network error, it handles it accordingly.
       */
      if (err.response?.status === 401) {
        // if (refreshCount < APIConfig.MAX_REFRESH_ATTEMPTS) {
        //   const refreshToken = cookie.get(CookieKeys.Auth);
        //   // const refreshToken = await AsyncStorageUtils.getRefreshToken()
        //   if (refreshToken && refreshToken !== "undefined")
        //     await refreshAuthToken(refreshToken);
        //   refreshCount++;
        //   // pass the control back to network manager
        //   return await request(body, params);
        // } else {
        //   // Stores.dispatch(AppActions.LOGOUT())
        //   // UserState.observeLogout();
        // }
        localStorage.clear();
      } else if (err.code === HTTP_STATUS.NETWORK_ERR) {
        apiError("Internal server error!");
      }

      return new APIError(
        err?.response?.data?.errors,
        err.response?.data?.statusCode,
        err?.response?.data?.message
      );
    }
  }
  return {
    request,
  };
}

//#region Prepare endpoint url with params

/**
 * Prepare the payload for the HTTP request.
 *
 * @param body - The request body as a Record<string, any> object.
 * @param withFile - A boolean indicating whether the request includes a file.
 * @returns The prepared payload for the HTTP request.
 */
function httpPayloadBuilder(body: Record<string, any>, withFile: boolean) {
  if (withFile) {
    const formData = new FormData() as FormData;
    for (let key in body) {
      if (body[key] instanceof FileList) {
        const file = body[key];
        formData.append(key, file);
      } else {
        formData.append(key, body[key]);
      }
    }
    return formData;
  } else {
    return body;
  }
}
